import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Mail, Send, Users, Briefcase, Code, Trophy, Upload, 
  ChevronDown, Loader2, CheckCircle, AlertCircle, FileText,
  CreditCard, Plus, Search, Filter, X, Eye
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CompanyEmailCenterPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [credits, setCredits] = useState({ available_credits: 0, total_purchased: 0, total_used: 0 });
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  
  // Email compose state
  const [audienceType, setAudienceType] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [jobId, setJobId] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState(new Set());
  
  // Custom emails
  const [customEmails, setCustomEmails] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  
  // Email content
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  
  // Approval status
  const [approvalStatus, setApprovalStatus] = useState({ email_approved: false, loading: true });
  const [requestingApproval, setRequestingApproval] = useState(false);
  
  // Available challenges and jobs
  const [challenges, setChallenges] = useState([]);
  const [jobs, setJobs] = useState([]);
  
  const token = localStorage.getItem('company_token');
  const company = JSON.parse(localStorage.getItem('company') || '{}');

  useEffect(() => {
    if (!token) {
      navigate('/company/auth');
      return;
    }
    fetchCredits();
    fetchTemplates();
    fetchChallengesAndJobs();
    fetchApprovalStatus();
  }, [token, navigate]);

  const fetchApprovalStatus = async () => {
    try {
      const res = await axios.get(`${API}/api/company/email/approval-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApprovalStatus({ ...res.data, loading: false });
    } catch (err) {
      console.error('Failed to fetch approval status:', err);
      setApprovalStatus({ email_approved: false, loading: false });
    }
  };

  const requestApproval = async () => {
    setRequestingApproval(true);
    try {
      const res = await axios.post(`${API}/api/company/email/request-approval`, 
        { reason: "Request for bulk email sending approval for candidate outreach" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Approval request submitted successfully!');
      fetchApprovalStatus();
    } catch (err) {
      toast.error('Failed to submit approval request');
    } finally {
      setRequestingApproval(false);
    }
  };

  const fetchCredits = async () => {
    try {
      const res = await axios.get(`${API}/api/company/email/credits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCredits(res.data);
    } catch (err) {
      console.error('Failed to fetch credits:', err);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/api/company/email-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(res.data.templates || []);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const fetchChallengesAndJobs = async () => {
    try {
      // Fetch hiring challenges
      const challengesRes = await axios.get(`${API}/api/company/hiring-challenges`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChallenges(challengesRes.data.challenges || []);
      
      // Fetch jobs
      const jobsRes = await axios.get(`${API}/api/company/jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(jobsRes.data.jobs || []);
    } catch (err) {
      console.error('Failed to fetch challenges/jobs:', err);
    }
  };

  const fetchRecipients = async () => {
    if (!audienceType || audienceType === 'custom') return;
    
    setLoadingRecipients(true);
    try {
      let url = `${API}/api/company/email/recipients-preview?audience_type=${audienceType}`;
      if (challengeId) url += `&challenge_id=${challengeId}`;
      if (jobId) url += `&job_id=${jobId}`;
      
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setRecipients(res.data.recipients || []);
      // Select all by default
      setSelectedRecipients(new Set(res.data.recipients.map(r => r.id)));
    } catch (err) {
      console.error('Failed to fetch recipients:', err);
      toast.error('Failed to load recipients');
    } finally {
      setLoadingRecipients(false);
    }
  };

  useEffect(() => {
    if (audienceType && audienceType !== 'custom') {
      fetchRecipients();
    } else {
      setRecipients([]);
      setSelectedRecipients(new Set());
    }
  }, [audienceType, challengeId, jobId]);

  const handleTemplateSelect = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(templateId);
      setSubject(template.subject.replace('{{company_name}}', company.company_name || 'Company'));
      setBody(template.body.replace('{{company_name}}', company.company_name || 'Company'));
    }
  };

  const toggleRecipient = (id) => {
    const newSelected = new Set(selectedRecipients);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRecipients(newSelected);
  };

  const selectAll = () => {
    setSelectedRecipients(new Set(recipients.map(r => r.id)));
  };

  const deselectAll = () => {
    setSelectedRecipients(new Set());
  };

  const getRecipientCount = () => {
    if (audienceType === 'custom') {
      const emails = customEmails.split(/[\n,]/).filter(e => e.trim() && e.includes('@'));
      return emails.length;
    }
    return selectedRecipients.size;
  };

  const handleSend = async () => {
    const recipientCount = getRecipientCount();
    
    if (recipientCount === 0) {
      toast.error('Please select at least one recipient');
      return;
    }
    
    if (!subject.trim() || !body.trim()) {
      toast.error('Please fill in subject and message');
      return;
    }
    
    if (credits.available_credits < recipientCount) {
      toast.error(`Insufficient credits. You have ${credits.available_credits} but need ${recipientCount}`);
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        audience_type: audienceType,
        subject,
        message: body,
        template_id: selectedTemplate
      };
      
      if (audienceType === 'custom') {
        const emails = customEmails.split(/[\n,]/).filter(e => e.trim() && e.includes('@')).map(e => e.trim());
        payload.custom_emails = emails;
      } else {
        payload.target_ids = Array.from(selectedRecipients);
        if (challengeId) payload.challenge_id = challengeId;
        if (jobId) payload.job_id = jobId;
      }
      
      const res = await axios.post(`${API}/api/company/email/send-bulk`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Successfully sent ${res.data.sent} emails!`);
      fetchCredits();
      
      // Reset form
      setSubject('');
      setBody('');
      setSelectedTemplate('');
      setSelectedRecipients(new Set());
      setCustomEmails('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send emails');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Mail className="text-purple-600" size={28} />
              Email Center
            </h1>
            <p className="text-gray-500 text-sm mt-1">Send bulk emails to candidates and participants</p>
          </div>
          
          {/* Credits Display */}
          <div className="flex items-center gap-4">
            <div className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white px-4 py-2 rounded-lg">
              <div className="flex items-center gap-2">
                <CreditCard size={18} />
                <span className="font-semibold">{credits.available_credits} Credits</span>
              </div>
            </div>
            <button
              onClick={() => navigate('/company/subscription')}
              className="flex items-center gap-1 px-3 py-2 border border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 text-sm font-medium"
            >
              <Plus size={16} />
              Buy Credits
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Approval Status Banner */}
        {!approvalStatus.loading && !approvalStatus.email_approved && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="text-amber-600 mt-0.5" size={20} />
              <div className="flex-1">
                <h3 className="font-semibold text-amber-800">Email Approval Required</h3>
                <p className="text-amber-700 text-sm mt-1">
                  Your company needs admin approval before sending bulk emails. This helps prevent spam and ensures quality communication.
                </p>
                <button
                  onClick={requestApproval}
                  disabled={requestingApproval}
                  className="mt-3 px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400 text-white rounded-lg text-sm font-medium flex items-center gap-2"
                >
                  {requestingApproval ? (
                    <>
                      <Loader2 className="animate-spin" size={16} />
                      Requesting...
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      Request Email Approval
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {approvalStatus.email_approved && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-2">
            <CheckCircle className="text-green-600" size={18} />
            <span className="text-green-800 text-sm font-medium">Your company is approved for bulk email sending</span>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Panel - Audience Selection */}
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Users size={18} className="text-purple-600" />
                Select Audience
              </h3>
              
              <div className="space-y-2">
                {[
                  { id: 'applied_candidates', label: 'Job Applicants', icon: Briefcase, desc: 'Candidates who applied to your jobs' },
                  { id: 'challenge_participants', label: 'Challenge Participants', icon: Code, desc: 'Participants in your hiring challenges' },
                  { id: 'candidate_pool', label: 'Candidate Pool', icon: Users, desc: 'Your imported candidate list' },
                  { id: 'custom', label: 'Custom Emails', icon: Mail, desc: 'Enter email addresses manually' }
                ].map(audience => (
                  <button
                    key={audience.id}
                    onClick={() => {
                      setAudienceType(audience.id);
                      setShowCustomInput(audience.id === 'custom');
                    }}
                    className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                      audienceType === audience.id
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <audience.icon size={20} className={audienceType === audience.id ? 'text-purple-600' : 'text-gray-400'} />
                      <div>
                        <p className="font-medium text-gray-900">{audience.label}</p>
                        <p className="text-xs text-gray-500">{audience.desc}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              
              {/* Challenge/Job selector */}
              {audienceType === 'challenge_participants' && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select Challenge</label>
                  <select
                    value={challengeId}
                    onChange={(e) => setChallengeId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Select a challenge...</option>
                    {challenges.map(c => (
                      <option key={c.id} value={c.id}>{c.title}</option>
                    ))}
                  </select>
                </div>
              )}
              
              {audienceType === 'applied_candidates' && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Job (Optional)</label>
                  <select
                    value={jobId}
                    onChange={(e) => setJobId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">All Jobs</option>
                    {jobs.map(j => (
                      <option key={j.id} value={j.id}>{j.title}</option>
                    ))}
                  </select>
                </div>
              )}
              
              {/* Custom emails input */}
              {showCustomInput && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Enter Email Addresses
                  </label>
                  <textarea
                    value={customEmails}
                    onChange={(e) => setCustomEmails(e.target.value)}
                    placeholder="Enter emails separated by comma or new line&#10;email1@example.com&#10;email2@example.com"
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {customEmails.split(/[\n,]/).filter(e => e.trim() && e.includes('@')).length} valid emails detected
                  </p>
                </div>
              )}
            </div>
            
            {/* Recipients Preview */}
            {audienceType && audienceType !== 'custom' && (
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900 text-sm">
                    Recipients ({selectedRecipients.size}/{recipients.length})
                  </h3>
                  <div className="flex gap-2">
                    <button onClick={selectAll} className="text-xs text-purple-600 hover:underline">Select All</button>
                    <button onClick={deselectAll} className="text-xs text-gray-500 hover:underline">Clear</button>
                  </div>
                </div>
                
                {loadingRecipients ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="animate-spin text-purple-600" size={24} />
                  </div>
                ) : recipients.length === 0 ? (
                  <div className="text-center py-6 text-gray-500 text-sm">
                    {challengeId || jobId ? 'No recipients found' : 'Select a challenge or job to see recipients'}
                  </div>
                ) : (
                  <div className="max-h-64 overflow-y-auto space-y-1">
                    {recipients.map(r => (
                      <label
                        key={r.id}
                        className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={selectedRecipients.has(r.id)}
                          onChange={() => toggleRecipient(r.id)}
                          className="w-4 h-4 text-purple-600 rounded"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{r.name || 'Unknown'}</p>
                          <p className="text-xs text-gray-500 truncate">{r.email}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Panel - Email Compose */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FileText size={18} className="text-purple-600" />
                Compose Email
              </h3>
              
              {/* Template Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Use Template (Optional)</label>
                <select
                  value={selectedTemplate}
                  onChange={(e) => handleTemplateSelect(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Custom Email</option>
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              
              {/* Subject */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Email subject line"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              
              {/* Body */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Write your email message here...&#10;&#10;Available variables: {{candidate_name}}, {{company_name}}"
                  rows={12}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Use {`{{candidate_name}}`} and {`{{company_name}}`} for personalization
                </p>
              </div>
              
              {/* Send Button */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  <span className="font-medium">{getRecipientCount()}</span> recipients selected
                  <span className="mx-2">•</span>
                  <span className="font-medium">{getRecipientCount()}</span> credits will be used
                </div>
                
                <button
                  onClick={handleSend}
                  disabled={loading || getRecipientCount() === 0 || !approvalStatus.email_approved}
                  className="flex items-center gap-2 px-6 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white rounded-lg font-medium transition-colors"
                  title={!approvalStatus.email_approved ? "Email approval required" : ""}
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin" size={18} />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Send Emails
                    </>
                  )}
                </button>
              </div>
            </div>
            
            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                <p className="text-2xl font-bold text-purple-600">{credits.available_credits}</p>
                <p className="text-sm text-gray-500">Available Credits</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{credits.total_used}</p>
                <p className="text-sm text-gray-500">Emails Sent</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                <p className="text-2xl font-bold text-gray-900">{templates.length}</p>
                <p className="text-sm text-gray-500">Templates</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyEmailCenterPage;
